/**
 * Утилиты для панелей внутри компонента Tiles (WUI-373).
 * Swing: у каждой строки UniDataTable — копия одной панели с тем же panel id/name.
 */

import {
  fieldEntryHasPresentableValue,
} from '@/common/utils/changedDataFieldMerge'
import {
  mergePageFieldMaps,
  type MergePageFieldMapsOptions,
} from '@/common/utils/mergePageFieldMaps'
import { omitLeakedTableColumnFieldsFromElementSlot } from '@/common/utils/omitTableColumnFieldsFromElementSlot'
import { convertRowDataToFieldsForScript } from '@/common/utils/rowDataUtils'
import { isTilesRowScopedRegistryName } from '@/common/utils/tilesSetInlineVisibility'

export interface SwingTilesRowScopeLike {
  tableFieldName: string
  rowIndex: number
  tilesElementId?: number
  parentPanelId?: string | number
  commitRowField?: (fieldName: string, value: unknown) => void
  getRowFieldValue?: (fieldName: string) => unknown
  getRowData?: () => unknown
  /** WUI-408: override видимости action в строке (из MTiles.setInlineVisibility). */
  getActionVisibility?: (actionName: string) => boolean | undefined
}

type MergeFn = (
  ...maps: Array<Record<string, unknown> | MergePageFieldMapsOptions | undefined>
) => Record<string, unknown>

/** Префикс field/registry name: `serviceTypes__row0__`. */
export function buildTilesRowRegistryPrefix(tableFieldName: string, rowIndex: number): string {
  const table = String(tableFieldName ?? '').trim()
  const index = Number(rowIndex)
  if (!table || !Number.isFinite(index) || index < 0) return ''
  return `${table}__row${index}__`
}

/** Ключ bucket в forms[formId][bucketKey][elementId] для изоляции данных строки. */
export function resolveTilesPanelFormsStorageKey(
  panelId: string | number,
  scope: SwingTilesRowScopeLike | null | undefined,
): string {
  const pid = String(panelId ?? '').trim()
  if (!scope) return pid
  const rowKey = `${String(scope.tableFieldName ?? '').trim()}__row${scope.rowIndex}`
  if (!rowKey || rowKey === '__rowNaN') return pid
  return `${pid}~${rowKey}`
}

/** Все bucket-ключи панели (основной + строки Tiles). */
export function listPanelFormsStorageKeys(
  panelId: string | number,
  panelKeys: Iterable<string>,
): string[] {
  const pid = String(panelId ?? '').trim()
  const prefix = `${pid}~`
  const result = new Set<string>()
  for (const key of panelKeys) {
    if (key === pid || key.startsWith(prefix)) {
      result.add(key)
    }
  }
  if (result.size === 0 && panelKeys instanceof Object) {
    const keys = panelKeys as string[]
    if (keys.includes(pid)) result.add(pid)
  }
  return [...result]
}

/** Убрать префикс строки из имён полей для putPanelData (Swing: `_id`, `content`, …). */
export function stripTilesRowFieldPrefix(
  fields: Record<string, unknown>,
  scope: SwingTilesRowScopeLike,
): Record<string, unknown> {
  const prefix = buildTilesRowRegistryPrefix(scope.tableFieldName, scope.rowIndex)
  if (!prefix) return { ...fields }

  const result: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(fields)) {
    if (key.startsWith(prefix)) {
      result[key.slice(prefix.length)] = value
      continue
    }
    // Поля без префикса в bucket строки (Identificator _id и т.п.)
    if (!key.includes('__row')) {
      result[key] = value
    }
  }
  return result
}

export function readTilesRowScopeFromCtx(ctx: unknown): SwingTilesRowScopeLike | null {
  if (!ctx || typeof ctx !== 'object') return null
  const scope = (ctx as { tilesRowScope?: SwingTilesRowScopeLike }).tilesRowScope
  if (!scope?.tableFieldName || !Number.isFinite(Number(scope.rowIndex))) return null
  return scope
}

/** instanceKey панели Tiles: `serviceTypes__row0` → bucket `4293~serviceTypes__row0`. */
export function resolvePanelInstanceKeyToFormsBucket(
  panelId: string | number,
  instanceKey: string | undefined,
  defaultKey = 'default',
): string | null {
  const pid = String(panelId ?? '').trim()
  const ik = String(instanceKey ?? '').trim()
  if (!ik || ik === defaultKey) return null
  return `${pid}~${ik}`
}

export function resolveBasePanelIdFromFormsBucket(bucketKey: string): string {
  const key = String(bucketKey ?? '').trim()
  const sep = key.indexOf('~')
  return sep >= 0 ? key.slice(0, sep) : key
}

export function shouldIncludeFormsBucketForTilesScope(
  bucketKey: string,
  allBucketKeys: Iterable<string>,
  scope: SwingTilesRowScopeLike | null | undefined,
): boolean {
  if (!scope?.tableFieldName || !Number.isFinite(Number(scope.rowIndex))) {
    return true
  }
  const key = String(bucketKey ?? '').trim()
  if (!key) return false
  const basePanelId = resolveBasePanelIdFromFormsBucket(key)
  const scopedKey = resolveTilesPanelFormsStorageKey(basePanelId, scope)
  const keys = new Set(Array.from(allBucketKeys, (k) => String(k ?? '').trim()).filter(Boolean))

  if (key.includes('~')) {
    return key === scopedKey
  }

  if (keys.has(scopedKey)) {
    return false
  }
  return true
}

export function mergeFormsPanelBucketsForGetForm(
  formPanels: Record<string, Record<string, Record<string, unknown>> | unknown>,
  options?: {
    tilesRowScope?: SwingTilesRowScopeLike | null
    isPanelVisible?: (bucketKey: string, basePanelId: string) => boolean
    mergeFieldMaps?: MergeFn
  },
): Record<string, unknown> {
  const merge: MergeFn = options?.mergeFieldMaps ?? mergePageFieldMaps
  const scope = options?.tilesRowScope ?? null
  const bucketKeys = Object.keys(formPanels ?? {})
  let formData: Record<string, unknown> = {}

  for (const bucketKey of bucketKeys) {
    if (!shouldIncludeFormsBucketForTilesScope(bucketKey, bucketKeys, scope)) {
      continue
    }
    const basePanelId = resolveBasePanelIdFromFormsBucket(bucketKey)
    if (options?.isPanelVisible && !options.isPanelVisible(bucketKey, basePanelId)) {
      continue
    }
    const panelData = formPanels[bucketKey]
    if (!panelData || typeof panelData !== 'object' || Array.isArray(panelData)) {
      continue
    }
    let panelFields: Record<string, unknown> = {}
    for (const [, elementData] of Object.entries(panelData as Record<string, unknown>)) {
      if (elementData && typeof elementData === 'object' && !Array.isArray(elementData)) {
        panelFields = merge(
          panelFields,
          omitLeakedTableColumnFieldsFromElementSlot(elementData as Record<string, unknown>),
        )
      }
    }
    if (scope && bucketKey.includes('~')) {
      panelFields = stripTilesRowFieldPrefix(panelFields, scope)
    }
    formData = merge(formData, panelFields)
  }

  return formData
}

const TILES_ROW_IDENTITY_KEYS = ['_id', '_typeId', '_versionId'] as const

/** PK строки tiles — не dump всей UniDataTable-row (statusId__listbox заказа, totalAmount, …). */
export function pickTilesRowIdentityFields(rowData: unknown): Record<string, unknown> {
  if (rowData == null || typeof rowData !== 'object') return {}
  const converted = convertRowDataToFieldsForScript(rowData) as Record<string, unknown>
  const out: Record<string, unknown> = {}
  for (const key of TILES_ROW_IDENTITY_KEYS) {
    if (converted[key] != null) {
      out[key] = converted[key]
    }
  }
  return out
}

export function overlayTilesRowIdentityOnFieldMap(
  fields: Record<string, unknown>,
  sources: Array<unknown>,
): Record<string, unknown> {
  const identity: Record<string, unknown> = {}
  for (const source of sources) {
    Object.assign(identity, pickTilesRowIdentityFields(source))
  }
  if (Object.keys(identity).length === 0) {
    return fields
  }
  return { ...fields, ...identity }
}

/** Строки UniDataTable `{name}` из снимка формы (putFormData), не dump колонок в корень. */
export function readTilesTableRowsFromFieldMap(
  fields: Record<string, unknown>,
  tableFieldName: string,
): unknown[] {
  const name = String(tableFieldName ?? '').trim()
  if (!name) return []
  const entry = fields[name]
  if (entry == null) return []
  const raw =
    entry &&
    typeof entry === 'object' &&
    !Array.isArray(entry) &&
    'value' in (entry as object) &&
    'type' in (entry as object)
      ? (entry as { value: unknown }).value
      : entry
  if (raw && typeof raw === 'object' && Array.isArray((raw as { rows?: unknown[] }).rows)) {
    return (raw as { rows: unknown[] }).rows
  }
  return []
}

/** PK текущей tiles-строки из уже собранной таблицы формы. */
export function pickTilesRowIdentityFromCollectedTable(
  fields: Record<string, unknown>,
  scope: SwingTilesRowScopeLike | null | undefined,
): Record<string, unknown> {
  if (!scope) return {}
  const rows = readTilesTableRowsFromFieldMap(fields, String(scope.tableFieldName ?? '').trim())
  const idx = Number(scope.rowIndex)
  if (!Number.isFinite(idx) || idx < 0 || idx >= rows.length) return {}
  return pickTilesRowIdentityFields(rows[idx])
}

/**
 * Live/registry-поля других клонов (`selectedServices__row8__calcRemoveDate`) в payload этой строки
 * не входят. Свой префикс снимается, как в putPanelData Swing.
 */
export function filterFieldMapToCurrentTilesRow(
  fields: Record<string, unknown>,
  scope: SwingTilesRowScopeLike,
): Record<string, unknown> {
  return stripTilesRowFieldPrefix(fields, scope)
}

/**
 * Live-overrides собираются со всей формы. Для tiles-клона нельзя оставлять
 * беспрефиксные поля (`statusId__listbox`, `totalAmount`) — это ComboBox/сумма
 * **заказа**, не `panel.getData` клона. Только ключи текущего `__rowN__`.
 */
export function filterLiveOverridesToCurrentTilesRowPrefix(
  fields: Record<string, unknown>,
  scope: SwingTilesRowScopeLike,
): Record<string, unknown> {
  const prefix = buildTilesRowRegistryPrefix(scope.tableFieldName, scope.rowIndex)
  if (!prefix) return {}
  const out: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(fields)) {
    if (key.startsWith(prefix)) {
      out[key.slice(prefix.length)] = value
    }
  }
  return out
}

/** Bucket `panelId~table__rowN` для таблиц из authoritative reload. */
export function listTilesCloneFormsBucketKeys(
  bucketKeys: Iterable<string>,
  tableFieldNames: ReadonlySet<string>,
): string[] {
  if (!tableFieldNames || tableFieldNames.size === 0) return []
  const out: string[] = []
  for (const raw of bucketKeys) {
    const key = String(raw ?? '').trim()
    const sep = key.indexOf('~')
    if (sep < 0) continue
    const rowPart = key.slice(sep + 1)
    for (const table of tableFieldNames) {
      const prefix = `${String(table ?? '').trim()}__row`
      if (!prefix.startsWith('__row') && rowPart.startsWith(prefix) && /^\d+$/.test(rowPart.slice(prefix.length))) {
        out.push(key)
        break
      }
    }
  }
  return out
}

export function pruneTilesCloneFormsBuckets(
  formPanels: Record<string, unknown> | null | undefined,
  tableFieldNames: ReadonlySet<string>,
): string[] {
  if (!formPanels || typeof formPanels !== 'object') return []
  const keys = listTilesCloneFormsBucketKeys(Object.keys(formPanels), tableFieldNames)
  for (const key of keys) {
    delete formPanels[key]
  }
  return keys
}

type TilesCloneWidgetRef = {
  panelId?: unknown
  fieldName?: unknown
  name?: unknown
  elementType?: unknown
  type?: unknown
  getType?: () => unknown
  ctx?: unknown
}

function readRegisteredWidgetType(el: TilesCloneWidgetRef): string {
  const raw =
    el.elementType ??
    el.type ??
    (typeof el.getType === 'function' ? el.getType() : '')
  return String(raw ?? '')
    .trim()
    .toUpperCase()
}

/** Клон строки Tiles (scoped registry name или ctx.tilesRowScope), не сам виджет TILES. */
export function shouldSkipTilesCloneScalarRefill(el: {
  elementType?: unknown
  type?: unknown
  getType?: () => unknown
  fieldName?: unknown
  name?: unknown
  ctx?: unknown
  incomingIsTable?: boolean
}): boolean {
  if (el.incomingIsTable) return false
  if (readRegisteredWidgetType(el) === 'TILES') return false
  if (readTilesRowScopeFromCtx(el.ctx)) return true
  const fname = String(el.fieldName ?? el.name ?? '').trim()
  return isTilesRowScopedRegistryName(fname)
}

function stripTilesRowPrefixFromFieldName(fieldName: string, tableFieldName: string): string {
  const fname = String(fieldName ?? '').trim()
  const table = String(tableFieldName ?? '').trim()
  if (!fname) return ''
  if (table) {
    const prefixBase = `${table}__row`
    if (fname.startsWith(prefixBase)) {
      const after = fname.slice(prefixBase.length)
      const sep = after.indexOf('__')
      if (sep >= 0 && /^\d+$/.test(after.slice(0, sep))) {
        return after.slice(sep + 2)
      }
    }
  }
  const generic = fname.match(/^.+__row\d+__(.+)$/)
  return generic?.[1] || fname
}

/** Имена виджетов шаблона клона (без префикса строки) — не колонки всей UniDataTable-row. */
export function collectTilesCloneWidgetFieldNames(
  elements: Iterable<TilesCloneWidgetRef>,
  scope: SwingTilesRowScopeLike,
  panelId: string | number,
): string[] {
  const pid = String(panelId ?? '').trim()
  const table = String(scope.tableFieldName ?? '').trim()
  if (!pid || !table) return []
  const names = new Set<string>()
  for (const el of elements) {
    if (String(el.panelId ?? '').trim() !== pid) continue
    const fname = String(el.fieldName ?? el.name ?? '').trim()
    if (!fname) continue
    const elScope = readTilesRowScopeFromCtx(el.ctx)
    const isThisTable =
      (elScope && String(elScope.tableFieldName ?? '').trim() === table) ||
      fname.startsWith(`${table}__row`)
    if (!isThisTable) continue
    const stripped = stripTilesRowPrefixFromFieldName(fname, table)
    if (stripped) names.add(stripped)
  }
  return [...names]
}

function wrapTilesRowFieldEntry(raw: unknown): unknown {
  if (raw == null) return undefined
  if (typeof raw === 'object' && !Array.isArray(raw) && 'value' in (raw as object)) {
    return raw
  }
  const type =
    typeof raw === 'number' ? 'Long' : typeof raw === 'boolean' ? 'Boolean' : 'String'
  return { type, value: raw }
}

export function resolveTilesCloneRowFieldRaw(
  scope: SwingTilesRowScopeLike | null | undefined,
  fieldName: string,
): unknown {
  if (!scope) return undefined
  const name = String(fieldName ?? '').trim()
  if (!name) return undefined
  if (typeof scope.getRowFieldValue === 'function') {
    try {
      const raw = scope.getRowFieldValue(name)
      if (raw !== undefined && raw !== null) {
        if (typeof raw === 'object' && !Array.isArray(raw) && 'value' in (raw as object)) {
          return (raw as { value: unknown }).value
        }
        return raw
      }
    } catch {
      /* row not ready */
    }
  }
  if (typeof scope.getRowData !== 'function') return undefined
  try {
    const fields = convertRowDataToFieldsForScript(scope.getRowData()) as Record<string, unknown>
    const entry = fields[name]
    if (entry && typeof entry === 'object' && !Array.isArray(entry) && 'value' in entry) {
      return (entry as { value: unknown }).value
    }
    return entry
  } catch {
    return undefined
  }
}

/**
 * putPanelData / getForm клона: недостающие поля виджетов — из строки, не dump всей row.
 */
export function overlayTilesCloneWidgetFieldsFromRow(
  panelFields: Record<string, unknown>,
  scope: SwingTilesRowScopeLike,
  widgetFieldNames: Iterable<string>,
): Record<string, unknown> {
  const names = [...widgetFieldNames].map((n) => String(n ?? '').trim()).filter(Boolean)
  if (names.length === 0) return panelFields

  let rowFields: Record<string, unknown> = {}
  if (typeof scope.getRowData === 'function') {
    try {
      rowFields = convertRowDataToFieldsForScript(scope.getRowData()) as Record<string, unknown>
    } catch {
      rowFields = {}
    }
  }

  const out = { ...panelFields }
  for (const name of names) {
    if (fieldEntryHasPresentableValue(out[name])) continue
    let fromRow = rowFields[name]
    if (!fieldEntryHasPresentableValue(fromRow) && typeof scope.getRowFieldValue === 'function') {
      try {
        fromRow = wrapTilesRowFieldEntry(scope.getRowFieldValue(name))
      } catch {
        fromRow = undefined
      }
    }
    if (fieldEntryHasPresentableValue(fromRow)) {
      out[name] = fromRow
    }
  }
  return out
}

export function overlayTilesRowDataOnFormFields(
  formFields: Record<string, unknown>,
  scope: SwingTilesRowScopeLike | null | undefined,
  options?: {
    convertRowDataToFields?: (rowData: unknown) => Record<string, unknown>
    mergeFieldMaps?: MergeFn
  },
): Record<string, unknown> {
  if (!scope || typeof scope.getRowData !== 'function') {
    return formFields
  }
  let rowData: unknown
  try {
    rowData = scope.getRowData()
  } catch {
    return formFields
  }
  if (rowData == null) {
    return formFields
  }

  const convert =
    options?.convertRowDataToFields ??
    ((row: unknown) => convertRowDataToFieldsForScript(row) as Record<string, unknown>)
  const merge: MergeFn = options?.mergeFieldMaps ?? mergePageFieldMaps
  const identity = pickTilesRowIdentityFields(convert(rowData))
  if (Object.keys(identity).length === 0) {
    return formFields
  }
  return merge(formFields, identity)
}
